import { Capacitor } from "@capacitor/core";
import { Box, Container } from "@mui/material";
import { DateTime } from "luxon";
import { type FC, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { CustomCalendar } from "../../../components/CustomCalendar/CustomCalendar";
import { PanelDayFallback } from "../../../components/StaffingChanges/PanelDayFallback";
import { MobileNursesList } from "../../../components/staffing/mobile/NursesList/MobileNursesList";
import { HolidayDayBanner } from "../../../components/team/Holidays/schedule/HolidayDayBanner";
import { ScheduleHolidaysProvider } from "../../../components/team/Holidays/schedule/ScheduleHolidaysProvider";
import { useAuth } from "../../../contexts/AuthProvider";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import { useScheduleCacheEnabled } from "../../../hooks/useScheduleWeekRules";
import { useScheduleWeeks } from "../../../hooks/useScheduleWeeks";
import type { NurseInShift } from "../../../utils/models";
import { rowsOnDate, weekIndexOfDate } from "../../../utils/panelWeeks";
import { MobilePromotionBanner } from "../MobilePromotionBanner";

export const DayScheduleMobile: FC = () => {
	const { date: dateString } = useParams();
	const { chosenDepartment, refreshDepartmentData, refreshNursesData } = useHospitalData();
	const { userInfo } = useAuth();
	const [selectedDate, setSelectedDate] = useState<DateTime>(DateTime.fromISO(dateString as string));
	const hasFetchedRef = useRef(false);
	const isCacheEnabled = useScheduleCacheEnabled();
	// The day's weeks: the schedule cache with `staffing_schedule_cache` on (head nurses), the old list off.
	const schedule = useScheduleWeeks(selectedDate);

	// The old list is reloaded around the URL's day so it holds it; the cache asks for the day's weeks itself.
	useEffect(() => {
		if (!hasFetchedRef.current && !isCacheEnabled) {
			hasFetchedRef.current = true;
			init();
		}
	}, [isCacheEnabled]);

	const init = async (): Promise<void> => {
		await Promise.all([refreshDepartmentData(chosenDepartment?.department_id, true, selectedDate), refreshNursesData(chosenDepartment?.department_id)]);
	};

	const nurses = useMemo<NurseInShift[]>(() => {
		const week = schedule.weeks[weekIndexOfDate(schedule.weeks, selectedDate)];
		// Each row's own day for the date: a row can skip days, so the date's position differs between rows.
		const dayRows = week === undefined ? [] : rowsOnDate(week.weeklyShifts, selectedDate);
		const allNurses = dayRows.flatMap((row) => row.days.flatMap((day) => day.nurses)).filter((nurse) => !nurse.isPendingToDelete);

		const nurseId = userInfo.nurses_info?.find((nurseInfo) => nurseInfo.departments.includes(chosenDepartment.department_id))?.nurse.id;

		return allNurses.sort((a, b) => {
			if (nurseId !== undefined) {
				if (a.nurseId === nurseId && b.nurseId !== nurseId) return -1;
				if (a.nurseId !== nurseId && b.nurseId === nurseId) return 1;
			}
			if (a.charge && !b.charge) return -1;
			if (!a.charge && b.charge) return 1;

			const timeCompare: number = a.startTime.valueOf() - b.startTime.valueOf();
			if (timeCompare !== 0) return timeCompare;

			const roleCompare: number = b.role?.toLowerCase().localeCompare(a.role?.toLowerCase());
			if (roleCompare !== 0) return roleCompare;

			return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
		});
	}, [schedule.weeks, selectedDate, userInfo, chosenDepartment]);

	return (
		<ScheduleHolidaysProvider firstDate={schedule.firstDate} lastDate={schedule.lastDate}>
			<Box
				data-testid="day-schedule-mobile-root"
				sx={{
					flexGrow: 1,
					height: "calc(100vh - 50px)",
					overflow: "auto",
					bgcolor: "#F8F9FC",
					position: "absolute",
					top: "50px",
					left: 0,
					right: 0,
					bottom: 0,
					p: 0,
					m: 0,
					scrollBehavior: "smooth",
					overscrollBehavior: "contain",
				}}
			>
				<Container maxWidth="lg" sx={{ height: "100%", display: "flex", flexDirection: "column", p: 0, m: 0, overflowX: "hidden" }}>
					<Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5, width: "100%", mx: "auto", pb: 10 }}>
						<Box sx={{ position: "sticky", top: 0, width: "100%", zIndex: 5, bgcolor: "#F8F9FC" }}>
							<CustomCalendar shifts={schedule.viewerShifts} value={selectedDate} onChange={setSelectedDate} showOneWeek={true} showTitle={true} />
						</Box>
						{/* The mobile Team page has no Holidays tab, so the banner has no link here. */}
						<HolidayDayBanner date={selectedDate} showViewHolidays={false} sx={{ width: "100%", px: 2 }} dataTestId="mobile-day-holiday-banner" />
						<Box sx={{ width: "100%" }}>
							{schedule.isLoading || schedule.isError ? (
								<PanelDayFallback panelDay={schedule} dataTestId="day-schedule-mobile" />
							) : (
								<MobileNursesList nurses={nurses} />
							)}
						</Box>
					</Box>
				</Container>
			</Box>

			{!Capacitor.isNativePlatform() && <MobilePromotionBanner />}
		</ScheduleHolidaysProvider>
	);
};
